import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { App as CapacitorApp } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';
import { isNative } from './platform.js';
import { isAppleAndroidReturnLink } from './native/socialLogin.js';

/**
 * Integra el ciclo de vida nativo (Android e iOS, Capacitor) con la SPA — Fase 23,
 * Bloque B. **No-op en web** (`isNative === false`): no registra ningún listener.
 *
 * - **Back button** (`App.backButton`): si el WebView puede ir atrás en el
 *   historial (`canGoBack`), retrocede una entrada (igual que el back del browser);
 *   si está en la raíz, **minimiza** la app (no la cierra). Minimizar — en vez de
 *   `exitApp()` — evita matar una sesión de voz en curso sin confirmación
 *   (plan 23 B.8). Android sin este handler cerraría la Activity al primer back.
 * - **Deep links** (`App.appUrlOpen`): un link https de los emails (verify / reset /
 *   OTP) que abre la app entrega la URL acá; extraemos el path y lo enrutamos en
 *   react-router. Ver el intent-filter en `android/app/src/main/AndroidManifest.xml` y,
 *   en iOS, el entitlement `applinks:` (`ios/App/App/App.entitlements`) + el
 *   `apple-app-site-association` que sirve el backend (`routes/well-known.ts`).
 * - **Cold launch desde un link**: el evento `appUrlOpen` sale antes de que el plugin
 *   (y este listener) existan — en iOS el `SceneDelegate` lo reenvía durante
 *   `willConnectTo`, antes de que cargue el bridge —, así que se pierde. La URL queda
 *   en `App.getLaunchUrl()`: la leemos una vez por contexto JS y navegamos. Como
 *   `getLaunchUrl()` devuelve el ÚLTIMO link del proceso (también uno que llegó en
 *   caliente), tras un reload in-process (apply/rollback del updater OTA) volvería a
 *   devolverlo: `sessionStorage` recuerda el último link ya enrutado para no repetirlo.
 * - **Sign in with Apple on Android**: its return link (`/auth/apple/android-return`,
 *   plan 50) is consumed by the SocialLogin plugin's `openSecureWindow` and is never
 *   routed, warm or cold — it carries a single-use handoff (`isAppleAndroidReturnLink`).
 *
 * Debe montarse una sola vez, dentro del Router (usa `useNavigate`). Ver `App.tsx`.
 */
export function useNativeShell(): void {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isNative) return;

    // `addListener` es async (devuelve el handle por Promise). Si el efecto se
    // limpia antes de que resuelva, removemos el handle apenas llega (flag
    // `cancelled`) para no dejar listeners colgados en un remount.
    let cancelled = false;
    const handles: PluginListenerHandle[] = [];
    const track = (handle: PluginListenerHandle): void => {
      if (cancelled) void handle.remove();
      else handles.push(handle);
    };

    void CapacitorApp.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) window.history.back();
      else void CapacitorApp.minimizeApp();
    }).then(track);

    const openLink = (url: string): void => {
      if (isAppleAndroidReturnLink(url)) return;
      const path = toInAppPath(url);
      if (!path) return;
      rememberHandledLink(url);
      // `navigate` puede devolver `Promise<void>` en react-router 7 → `void` la
      // marca como ignorada (no la esperamos: es un side-effect de navegación).
      void navigate(path);
    };

    void CapacitorApp.addListener('appUrlOpen', ({ url }) => openLink(url)).then(track);

    if (!launchUrlChecked) {
      launchUrlChecked = true;
      // Sin chequear `cancelled` a propósito: el efecto se limpia apenas cambia
      // `navigate` (el primer redirect de la raíz), y el flag ya quedó consumido.
      void CapacitorApp.getLaunchUrl().then((launch) => {
        if (!launch?.url || launch.url === lastHandledLink()) return;
        openLink(launch.url);
      });
    }

    return () => {
      cancelled = true;
      for (const handle of handles) void handle.remove();
    };
  }, [navigate]);
}

/** Una sola lectura de `getLaunchUrl()` por contexto JS (el efecto se re-ejecuta). */
let launchUrlChecked = false;

const HANDLED_LINK_KEY = 'speak.nativeShell.lastLink';

function rememberHandledLink(url: string): void {
  try {
    sessionStorage.setItem(HANDLED_LINK_KEY, url);
  } catch {
    // Sin storage: el flag de módulo igual evita repetirlo dentro de este contexto.
  }
}

function lastHandledLink(): string | null {
  try {
    return sessionStorage.getItem(HANDLED_LINK_KEY);
  } catch {
    return null;
  }
}

/**
 * Extrae el path in-app (`/ruta?query#hash`) de la URL de un deep link https.
 * Devuelve `null` si la URL no parsea — el caller la ignora (cae al browser).
 */
function toInAppPath(rawUrl: string): string | null {
  try {
    const parsed = new URL(rawUrl);
    const path = `${parsed.pathname}${parsed.search}${parsed.hash}`;
    return path.startsWith('/') ? path : `/${path}`;
  } catch {
    return null;
  }
}
